import { DEFAULT_MODEL, REASONING_EFFORT_DEFAULT } from "../../../utils/ModelConfig";
import {
  EXTERNAL_CHAT_DEFAULT_ACCESS_POLICY,
  EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_UNIT,
  EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_VALUE,
  buildExternalAccessPolicy,
  parseOptionalPositiveInt,
  periodUnitToSeconds,
  splitPeriodSecondsIntoValueAndUnit,
} from "./externalAccess";
import { DEFAULT_EMAIL_RESPONSE_POLICY_PRESET } from "./emailPolicy";
import {
  KNOWLEDGE_INDEX_DEFAULT_STATUS,
  normalizeKnowledgeIndexStatus,
} from "./knowledgeStatus";
import {
  SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT,
  SELF_CONFIGURATION_MODE_AWARENESS_ONLY,
  SELF_CONFIGURATION_MODE_OFF,
  deriveSelfConfigurationModeFromEnabledTools,
  ensureSelfConfigurationToolsForMode,
  isSelfConfigurationAwarenessEnabled,
  isSelfConfigurationEditEnabled,
  normalizeSelfConfigurationMode,
} from "./selfConfiguration";
import {
  DEFAULT_ENABLED_TOOLS,
  INTERNAL_ONLY_TOOL_NAMES,
  ensureTaskQueuePlannerToolsPaired,
} from "./toolCatalog";

export const normalizeConfiguredTables = (values) => {
  const deduped = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const tableId = String(
      value?.tableId || value?.databaseId || value?.id || ""
    ).trim();
    if (!tableId || deduped.has(tableId)) return;
    const tableName = String(
      value?.tableName || value?.name || value?.databaseName || tableId
    ).trim();
    deduped.set(tableId, {
      tableId,
      tableName: tableName || tableId,
    });
  });
  return Array.from(deduped.values());
};

// mcpServerRefs are an explicit, per-tool allowlist: each ref carries the
// remote tool names the agent is allowed to call. The runtime enables ONLY
// tools whose remoteName is present in toolAllowlist, so a ref with an empty
// allowlist contributes nothing — those refs are dropped here.
// configuredDocs is stored in the draft as {docId, title} for display, but the
// API persists only a list of docId strings. normalizeConfiguredDocsForDraft
// accepts either shape (string ids from the API, or objects from the picker).
export const normalizeConfiguredDocsForDraft = (values) => {
  const deduped = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const docId =
      typeof value === "string"
        ? value.trim()
        : String(value?.docId || value?.id || "").trim();
    if (!docId || deduped.has(docId)) return;
    const title =
      typeof value === "string"
        ? ""
        : String(value?.title || value?.name || "").trim();
    deduped.set(docId, { docId, title });
  });
  return Array.from(deduped.values());
};

export const normalizeEnabledSkills = (values) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) =>
          String(typeof value === "string" ? value : value?.skillName || "")
            .trim()
            .toLowerCase()
        )
        .filter((value) => value.length > 0)
    )
  );

export const normalizeMcpServerRefs = (values) => {
  const deduped = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const serverId = String(value?.serverId || "").trim();
    if (!serverId) return;
    const toolAllowlist = Array.from(
      new Set(
        (Array.isArray(value?.toolAllowlist) ? value.toolAllowlist : [])
          .map((name) => String(name || "").trim())
          .filter((name) => name.length > 0)
      )
    );
    if (toolAllowlist.length === 0) return;
    deduped.set(serverId, { serverId, toolAllowlist });
  });
  return Array.from(deduped.values());
};

export const normalizeConfiguredAgents = (values) => {
  const deduped = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const selectedAgentId = String(value?.agentId || value?.id || "").trim();
    if (!selectedAgentId || deduped.has(selectedAgentId)) return;
    deduped.set(selectedAgentId, {
      agentId: selectedAgentId,
      name: String(value?.name || selectedAgentId).trim() || selectedAgentId,
      description: String(value?.description || "").trim(),
    });
  });
  return Array.from(deduped.values());
};

export const normalizeAvatarUrl = (value) => {
  const trimmed = String(value || "").trim();
  return trimmed || null;
};

export const createEmptyValidationDraft = () => ({
  enabled: false,
  trigger: "per_turn",
  type: "llm",
  completionCriteria: "",
  validationCode: "",
  validationModel: null,
  judgeAgentId: null,
  maxRetries: 3,
  onFail: "retry",
  hideFromChat: false,
});

const VALIDATION_TRIGGERS = new Set(["per_turn", "on_completion"]);
const VALIDATION_TYPES = new Set(["llm", "deterministic"]);
const VALIDATION_ON_FAIL = new Set(["retry", "fail", "continue"]);

export const validationDraftFromApi = (raw) => {
  const empty = createEmptyValidationDraft();
  if (!raw || typeof raw !== "object") return empty;
  const maxRetries = Number.parseInt(raw.maxRetries, 10);
  return {
    enabled: raw.enabled === true,
    trigger: VALIDATION_TRIGGERS.has(raw.trigger) ? raw.trigger : "per_turn",
    type: VALIDATION_TYPES.has(raw.type) ? raw.type : "llm",
    completionCriteria: String(raw.completionCriteria || ""),
    validationCode: String(raw.validationCode || ""),
    validationModel: raw.validationModel ? String(raw.validationModel) : null,
    judgeAgentId: raw.judgeAgentId ? String(raw.judgeAgentId) : null,
    maxRetries: Number.isFinite(maxRetries)
      ? Math.max(0, Math.min(10, maxRetries))
      : 3,
    onFail: VALIDATION_ON_FAIL.has(raw.onFail) ? raw.onFail : "retry",
    hideFromChat: raw.hideFromChat === true,
  };
};

export const validationPayloadFromDraft = (validation) => {
  const slice = validation || createEmptyValidationDraft();
  // Send an explicit disabled object rather than null when off. The backend update path skips
  // null fields (treats them as "unchanged"), so returning null here would leave a previously
  // enabled config in place. { enabled: false } normalizes to null server-side, clearing it.
  if (slice.enabled !== true) return { enabled: false };
  const maxRetries = Number.parseInt(slice.maxRetries, 10);
  const type = VALIDATION_TYPES.has(slice.type) ? slice.type : "llm";
  const completionCriteria = String(slice.completionCriteria || "").trim();
  const validationCode = String(slice.validationCode || "").trim();
  const validationModel = String(slice.validationModel || "").trim();
  const judgeAgentId = String(slice.judgeAgentId || "").trim();
  return {
    enabled: true,
    trigger: VALIDATION_TRIGGERS.has(slice.trigger) ? slice.trigger : "per_turn",
    type,
    completionCriteria: completionCriteria || null,
    validationCode: validationCode || null,
    validationModel: validationModel || null,
    // A judge agent only applies to the LLM judge; clear it for regex so switching
    // validation type never leaves a stale judge bound server-side.
    judgeAgentId: type === "llm" && judgeAgentId ? judgeAgentId : null,
    maxRetries: Number.isFinite(maxRetries)
      ? Math.max(0, Math.min(10, maxRetries))
      : 3,
    onFail: VALIDATION_ON_FAIL.has(slice.onFail) ? slice.onFail : "retry",
    hideFromChat: slice.hideFromChat === true,
  };
};

export const normalizeExamplePrompts = (values) =>
  (Array.isArray(values) ? values : [])
    .map((value) => ({
      title: String(value?.title || "")
        .trim()
        .slice(0, 80),
      prompt: String(value?.prompt || "")
        .trim()
        .slice(0, 500),
    }))
    .filter((value) => value.prompt.length > 0)
    .slice(0, 6);

export const normalizeKnowledgeTables = (values) => {
  const deduped = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const databaseId = String(
      value?.databaseId || value?.tableId || value?.id || ""
    ).trim();
    if (!databaseId || deduped.has(databaseId)) return;
    const tableName = String(
      value?.tableName || value?.name || value?.databaseName || databaseId
    ).trim();
    deduped.set(databaseId, {
      databaseId,
      tableName: tableName || databaseId,
    });
  });
  return Array.from(deduped.values());
};

export const normalizeReviewTaskUsers = (users) => {
  if (!Array.isArray(users)) return [];
  const seen = new Set();
  return users
    .filter((user) => user && user.roleDisabled !== true)
    .filter((user) => {
      const userName = String(user.userName || "").trim();
      if (!userName || seen.has(userName)) return false;
      seen.add(userName);
      return true;
    })
    .map((user) => ({
      ...user,
      userName: String(user.userName || "").trim(),
      displayName: String(
        user.displayName || user.name || user.fullName || user.userName || ""
      ).trim(),
      email: String(user.email || "").trim(),
    }));
};

export const createEmptyAgentDraft = () => ({
  identity: {
    isSystemAgent: false,
    isMasterAgent: false,
    name: "",
    description: "",
    avatarUrl: "",
    systemPrompt: "",
    model: DEFAULT_MODEL,
    reasoningEffort: REASONING_EFFORT_DEFAULT,
    maxIterations: 50,
  },
  tools: {
    enabledTools: [...DEFAULT_ENABLED_TOOLS],
    selfConfigurationMode: SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT,
    toolInstructions: {},
    sendSlackWebhookUrlsInput: "",
    runWorkflowConfigs: [],
    workflowScopeMode: "SELECTED",
    configuredTables: [],
    configuredAgents: [],
    mcpServerRefs: [],
    tableScopeMode: "SELECTED",
    agentScopeMode: "SELECTED",
    fileScopeMode: "SELECTED",
    artifactScopeMode: "SELECTED",
    docScopeMode: "ALL",
    configuredDocs: [],
    enabledSkills: [],
    approvalRequiredTools: [],
    scopeChangeRequiresApproval: true,
  },
  completion: {
    notificationType: "NONE",
    notifyEmails: "",
    slackWebhookUrl: "",
    webhookUrl: "",
  },
  validation: createEmptyValidationDraft(),
  knowledge: {
    retrievalMode: "RAG",
    files: [],
    tables: [],
    manualContext: "",
    indexStatus: { ...KNOWLEDGE_INDEX_DEFAULT_STATUS },
  },
  email: {
    inboxAddress: "",
    allowAllSenders: false,
    acceptEmailAttachments: false,
    senderWhitelist: "",
    responsePolicyPreset: DEFAULT_EMAIL_RESPONSE_POLICY_PRESET,
    responsePolicyCustomPrompt: "",
  },
  externalChat: {
    accessPolicy: EXTERNAL_CHAT_DEFAULT_ACCESS_POLICY,
    maxSessionsPerIdentity: "",
    maxSessionsTotal: "",
    rateLimitMessages: "",
    rateLimitPeriodValue: EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_VALUE,
    rateLimitPeriodUnit: EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_UNIT,
    rateLimitExceededMessage: "",
  },
  review: {
    escalationPolicy: "",
    blockedWhileReviewMessage: "",
    customBlockedWhileReviewMessageEnabled: false,
    autoReplyOnBlockedIngress: true,
    assigneeUserId: "",
    emailNotificationEnabled: false,
  },
  examplePrompts: [],
  publicShare: {
    link: null,
    error: "",
    expiresAt: "",
    enableExpiry: false,
  },
  chatUi: {
    showSystemPromptInChat: true,
    showToolCallsAndOutputsInChat: true,
    showInterAgentMessagesInChat: true,
    showWorkflowProvenanceInChat: true,
    showSchedulingActivityInChat: true,
    showSessionOperationalMetaInChat: true,
    showSupervisorControlMessagesInChat: true,
    enableHumanTakeoverMode: false,
    showSharedSessionsSidebar: true,
  },
  creation: {
    path: null,
    aiRequirements: "",
    stage: "idle",
    error: "",
  },
  ui: {
    activeTab: "instructions",
    saveResult: null,
    saveErrorMessage: "",
    deleteConfirmText: "",
  },
});

export const agentDraftFromApi = (data) => {
  const empty = createEmptyAgentDraft();
  const loadedEnabledTools = (data?.enabledTools || []).filter(
    (toolName) => !INTERNAL_ONLY_TOOL_NAMES.has(toolName)
  );
  const loadedMode = normalizeSelfConfigurationMode(
    data?.selfConfigurationMode
  );
  const derivedModeFromTools =
    deriveSelfConfigurationModeFromEnabledTools(loadedEnabledTools);
  const resolvedSelfConfigurationMode =
    isSelfConfigurationEditEnabled(loadedMode) ||
    isSelfConfigurationEditEnabled(derivedModeFromTools)
      ? SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
      : isSelfConfigurationAwarenessEnabled(loadedMode) ||
        isSelfConfigurationAwarenessEnabled(derivedModeFromTools)
      ? SELF_CONFIGURATION_MODE_AWARENESS_ONLY
      : SELF_CONFIGURATION_MODE_OFF;
  const knowledge = data?.knowledge || {};
  const loadedBlockedWhileReviewMessage = data?.blockedWhileReviewMessage || "";
  const loadedPeriod = splitPeriodSecondsIntoValueAndUnit(
    data?.externalChatRateLimitPeriodSeconds
  );
  const chatUiPolicy = data?.chatUiPolicy || {};

  return {
    ...empty,
    identity: {
      isSystemAgent: data?.systemAgent === true,
      isMasterAgent: data?.agentClass === "MASTER",
      name: data?.name || "",
      description: data?.description || "",
      avatarUrl: data?.avatarUrl || "",
      systemPrompt: data?.systemPrompt || "",
      model: data?.model || DEFAULT_MODEL,
      reasoningEffort: data?.reasoningEffort || REASONING_EFFORT_DEFAULT,
      maxIterations: data?.maxIterations || 50,
    },
    tools: {
      ...empty.tools,
      enabledTools: ensureSelfConfigurationToolsForMode(
        ensureTaskQueuePlannerToolsPaired(loadedEnabledTools),
        resolvedSelfConfigurationMode
      ),
      selfConfigurationMode: resolvedSelfConfigurationMode,
      toolInstructions: data?.toolInstructions || {},
      sendSlackWebhookUrlsInput: normalizeStringList(
        data?.sendSlackWebhookUrls
      ).join("\n"),
      runWorkflowConfigs: data?.runWorkflowConfigs || [],
      workflowScopeMode: data?.workflowScopeMode || "SELECTED",
      configuredTables: normalizeConfiguredTables(data?.configuredTables || []),
      configuredAgents: normalizeConfiguredAgents(data?.configuredAgents || []),
      mcpServerRefs: normalizeMcpServerRefs(data?.mcpServerRefs || []),
      tableScopeMode: data?.tableScopeMode || "SELECTED",
      agentScopeMode: data?.agentScopeMode || "SELECTED",
      fileScopeMode: data?.fileScopeMode || "SELECTED",
      artifactScopeMode: data?.artifactScopeMode || "SELECTED",
      docScopeMode: data?.docScopeMode || "ALL",
      configuredDocs: normalizeConfiguredDocsForDraft(data?.configuredDocs),
      enabledSkills: normalizeEnabledSkills(data?.enabledSkills),
      approvalRequiredTools: normalizeStringList(data?.approvalRequiredTools),
      // Null/absent → true: existing edit-mode agents pick up the safe default.
      scopeChangeRequiresApproval: data?.scopeChangeRequiresApproval !== false,
    },
    completion: {
      notificationType: data?.completionNotificationType || "NONE",
      notifyEmails: (data?.completionNotifyEmails || []).join(", "),
      slackWebhookUrl: data?.completionSlackWebhookUrl || "",
      webhookUrl: data?.completionWebhookUrl || "",
    },
    validation: validationDraftFromApi(data?.outputValidation),
    knowledge: {
      retrievalMode: "RAG",
      files: knowledge.files || [],
      tables: normalizeKnowledgeTables(knowledge.tables || []),
      manualContext: knowledge.manualContext || "",
      indexStatus: normalizeKnowledgeIndexStatus(data?.knowledgeIndexStatus),
    },
    email: {
      inboxAddress: data?.inboxAddress || "",
      allowAllSenders: data?.allowAllSenders || false,
      acceptEmailAttachments: data?.acceptEmailAttachments || false,
      senderWhitelist: (data?.senderWhitelist || []).join("\n"),
      responsePolicyPreset:
        data?.emailResponsePolicyPreset || DEFAULT_EMAIL_RESPONSE_POLICY_PRESET,
      responsePolicyCustomPrompt: data?.emailResponsePolicyCustomPrompt || "",
    },
    externalChat: {
      accessPolicy: buildExternalAccessPolicy(data?.externalAccessPolicy),
      maxSessionsPerIdentity: data?.externalChatMaxSessionsPerIdentity
        ? String(data.externalChatMaxSessionsPerIdentity)
        : "",
      maxSessionsTotal: data?.externalChatMaxSessionsTotal
        ? String(data.externalChatMaxSessionsTotal)
        : "",
      rateLimitMessages: data?.externalChatRateLimitMessages
        ? String(data.externalChatRateLimitMessages)
        : "",
      rateLimitPeriodValue: loadedPeriod.value,
      rateLimitPeriodUnit: loadedPeriod.unit,
      rateLimitExceededMessage:
        data?.externalChatRateLimitExceededMessage || "",
    },
    review: {
      escalationPolicy: data?.reviewEscalationPolicy || "",
      blockedWhileReviewMessage: loadedBlockedWhileReviewMessage,
      customBlockedWhileReviewMessageEnabled:
        loadedBlockedWhileReviewMessage.trim().length > 0,
      autoReplyOnBlockedIngress: data?.autoReplyOnBlockedIngress !== false,
      assigneeUserId: data?.reviewTaskAssigneeUserId || "",
      emailNotificationEnabled:
        data?.reviewTaskEmailNotificationEnabled === true,
    },
    examplePrompts: normalizeExamplePrompts(data?.examplePrompts),
    chatUi: {
      showSystemPromptInChat: chatUiPolicy.showSystemPromptInChat !== false,
      showToolCallsAndOutputsInChat:
        chatUiPolicy.showToolCallsAndOutputsInChat !== false,
      showInterAgentMessagesInChat:
        chatUiPolicy.showInterAgentMessagesInChat !== false,
      showWorkflowProvenanceInChat:
        chatUiPolicy.showWorkflowProvenanceInChat !== false,
      showSchedulingActivityInChat:
        chatUiPolicy.showSchedulingActivityInChat !== false,
      showSessionOperationalMetaInChat:
        chatUiPolicy.showSessionOperationalMetaInChat !== false,
      showSupervisorControlMessagesInChat:
        chatUiPolicy.showSupervisorControlMessagesInChat !== false,
      enableHumanTakeoverMode: chatUiPolicy.enableHumanTakeoverMode === true,
      showSharedSessionsSidebar:
        chatUiPolicy.showSharedSessionsSidebar !== false,
    },
  };
};

const normalizeStringList = (values) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter((value) => value.length > 0)
    )
  );

export const agentPayloadFromDraft = (draft, context = {}) => {
  const { parseSlackWebhookUrls, ensureSendEmailEnabledForPolicy } = context;
  const tools = draft.tools || {};
  const email = draft.email || {};
  const externalChat = draft.externalChat || {};
  const review = draft.review || {};
  const completion = draft.completion || {};
  const chatUi = draft.chatUi || {};
  const knowledge = draft.knowledge || {};
  const identity = draft.identity || {};
  let normalizedEnabledTools = (tools.enabledTools || []).filter(
    (toolName) => !INTERNAL_ONLY_TOOL_NAMES.has(toolName)
  );
  if (ensureSendEmailEnabledForPolicy) {
    normalizedEnabledTools = ensureSendEmailEnabledForPolicy(
      normalizedEnabledTools,
      email.responsePolicyPreset
    );
  }
  normalizedEnabledTools = ensureSelfConfigurationToolsForMode(
    normalizedEnabledTools,
    tools.selfConfigurationMode
  );
  normalizedEnabledTools = ensureTaskQueuePlannerToolsPaired(
    normalizedEnabledTools
  );
  const normalizedSendSlackWebhookUrls = parseSlackWebhookUrls
    ? parseSlackWebhookUrls(tools.sendSlackWebhookUrlsInput)
    : [];
  const normalizedRunWorkflowConfigs = (tools.runWorkflowConfigs || [])
    .filter((wf) => String(wf?.workflowId || "").trim().length > 0)
    .map((wf) => {
      const workflowId = String(wf.workflowId || "").trim();
      return {
        workflowId,
        workflowName:
          String(wf.workflowName || workflowId).trim() || workflowId,
        workflowDescription: String(wf.workflowDescription || "").trim(),
        published: wf.published === true,
      };
    });
  const normalizedConfiguredTables = normalizeConfiguredTables(
    tools.configuredTables
  );
  const normalizedConfiguredAgents = normalizeConfiguredAgents(
    tools.configuredAgents
  );
  const normalizedMcpServerRefs = normalizeMcpServerRefs(tools.mcpServerRefs);
  const normalizedKnowledgeTables = normalizeKnowledgeTables(knowledge.tables);
  const normalizedReviewTaskAssigneeUserId = String(
    review.assigneeUserId || ""
  ).trim();
  const effectiveEmailNotificationEnabled =
    review.emailNotificationEnabled && !!normalizedReviewTaskAssigneeUserId;
  const normalizedExternalAccessPolicy = buildExternalAccessPolicy(
    externalChat.accessPolicy
  );
  const periodValue = parseOptionalPositiveInt(
    externalChat.rateLimitPeriodValue
  );

  return {
    name: identity.name,
    description: identity.description,
    avatarUrl: normalizeAvatarUrl(identity.avatarUrl),
    systemPrompt: identity.systemPrompt,
    model: identity.model,
    reasoningEffort: identity.reasoningEffort || REASONING_EFFORT_DEFAULT,
    maxIterations: identity.maxIterations,
    selfConfigurationMode: tools.selfConfigurationMode,
    enabledTools: normalizedEnabledTools,
    toolInstructions: Object.fromEntries(
      Object.entries(tools.toolInstructions || {}).filter(
        ([, v]) => typeof v === "string" && v.trim().length > 0
      )
    ),
    sendSlackWebhookUrls: normalizedSendSlackWebhookUrls,
    runWorkflowConfigs: normalizedRunWorkflowConfigs,
    configuredTables: normalizedConfiguredTables,
    configuredAgents: normalizedConfiguredAgents,
    mcpServerRefs: normalizedMcpServerRefs,
    workflowScopeMode: tools.workflowScopeMode,
    tableScopeMode: tools.tableScopeMode,
    agentScopeMode: tools.agentScopeMode,
    fileScopeMode: tools.fileScopeMode,
    artifactScopeMode: tools.artifactScopeMode,
    docScopeMode: tools.docScopeMode || "ALL",
    configuredDocs: normalizeConfiguredDocsForDraft(tools.configuredDocs).map(
      (doc) => doc.docId
    ),
    enabledSkills: normalizeEnabledSkills(tools.enabledSkills),
    // Human-only approval policy. Only persist gates for tools that are still
    // equipped so unequipping a tool clears its gate.
    approvalRequiredTools: normalizeStringList(
      tools.approvalRequiredTools
    ).filter((toolName) => normalizedEnabledTools.includes(toolName)),
    scopeChangeRequiresApproval: tools.scopeChangeRequiresApproval !== false,
    completionNotificationType: completion.notificationType,
    completionNotifyEmails: String(completion.notifyEmails || "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0),
    completionSlackWebhookUrl: completion.slackWebhookUrl,
    completionWebhookUrl: completion.webhookUrl,
    outputValidation: validationPayloadFromDraft(draft.validation),
    knowledge: {
      retrievalMode: knowledge.retrievalMode || "RAG",
      files: knowledge.files || [],
      tables: normalizedKnowledgeTables,
      manualContext: knowledge.manualContext || "",
    },
    allowAllSenders: email.allowAllSenders,
    acceptEmailAttachments: email.acceptEmailAttachments,
    senderWhitelist: String(email.senderWhitelist || "")
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0),
    emailResponsePolicyPreset: email.responsePolicyPreset,
    emailResponsePolicyCustomPrompt: email.responsePolicyCustomPrompt,
    externalAccessPolicy: normalizedExternalAccessPolicy,
    externalChatAccessMode: normalizedExternalAccessPolicy.mode,
    externalChatMaxSessionsPerIdentity: parseOptionalPositiveInt(
      externalChat.maxSessionsPerIdentity
    ),
    externalChatMaxSessionsTotal: parseOptionalPositiveInt(
      externalChat.maxSessionsTotal
    ),
    externalChatRateLimitMessages:
      parseOptionalPositiveInt(externalChat.rateLimitMessages) ?? 0,
    externalChatRateLimitPeriodSeconds: periodValue
      ? periodValue * periodUnitToSeconds(externalChat.rateLimitPeriodUnit)
      : 0,
    externalChatRateLimitExceededMessage: String(
      externalChat.rateLimitExceededMessage || ""
    ).trim(),
    reviewEscalationPolicy: String(review.escalationPolicy || "").trim(),
    blockedWhileReviewMessage: review.customBlockedWhileReviewMessageEnabled
      ? String(review.blockedWhileReviewMessage || "").trim()
      : "",
    autoReplyOnBlockedIngress: review.autoReplyOnBlockedIngress,
    reviewTaskAssigneeUserId: normalizedReviewTaskAssigneeUserId,
    reviewTaskEmailNotificationEnabled: effectiveEmailNotificationEnabled,
    reviewTaskEmailNotifyUserId: effectiveEmailNotificationEnabled
      ? normalizedReviewTaskAssigneeUserId
      : "",
    examplePrompts: normalizeExamplePrompts(draft.examplePrompts),
    chatUiPolicy: {
      showSystemPromptInChat: chatUi.showSystemPromptInChat,
      showToolCallsAndOutputsInChat: chatUi.showToolCallsAndOutputsInChat,
      showInterAgentMessagesInChat: chatUi.showInterAgentMessagesInChat,
      showWorkflowProvenanceInChat: chatUi.showWorkflowProvenanceInChat,
      showSchedulingActivityInChat: chatUi.showSchedulingActivityInChat,
      showSessionOperationalMetaInChat: chatUi.showSessionOperationalMetaInChat,
      showSupervisorControlMessagesInChat:
        chatUi.showSupervisorControlMessagesInChat,
      enableHumanTakeoverMode: chatUi.enableHumanTakeoverMode,
      showSharedSessionsSidebar: chatUi.showSharedSessionsSidebar,
    },
  };
};
